/**
 * Container-children wire contract (R3 — task #138).
 *
 * WHAT
 * ────
 * A typed hint emitted by the backend converter (CanvasJsonMaterializer)
 * on container canvas nodes that tells the frontend IR builder which
 * peer nodes in the top-level `nodes` array really belong INSIDE that
 * container. Generalizes the existing `_loopBody` hint (shipped for
 * DO_WHILE in #122) to all five container kinds:
 *
 *   SWITCH               — children keyed by case-port (incl. `"default"`)
 *   FORK_JOIN            — children arrayed per branch (index-ordered)
 *   FORK_JOIN_DYNAMIC    — branchExpr + optional placeholder child refs
 *   DO_WHILE             — flat body child list (compatible with _loopBody)
 *   SUB_WORKFLOW         — referencedWorkflow + inline-embedded child refs
 *
 * WIRE SHAPE (per container-node)
 * ───────────────────────────────
 * SWITCH node:
 *   {
 *     id, type: 'condition',
 *     _containerChildren: {
 *       kind: 'switch',
 *       casePorts: {
 *         "<caseName>":  [canvasId, canvasId, ...],
 *         "default":     [canvasId, ...]
 *       },
 *       convergenceId: "<canvasId of // Marks end of switch pill>" | null
 *     }
 *   }
 *
 * FORK_JOIN node:
 *   {
 *     id, type: 'parallel_fork',
 *     _containerChildren: {
 *       kind: 'fork',
 *       branches: [
 *         { name: "branch_0", childIds: [canvasId, ...] },
 *         { name: "branch_1", childIds: [canvasId, ...] }
 *       ],
 *       joinId: "<canvasId of JOIN node>" | null
 *     }
 *   }
 *
 * FORK_JOIN_DYNAMIC node:
 *   {
 *     id, type: 'parallel_fork',  // marker tag on node tells dynamic
 *     _containerChildren: {
 *       kind: 'dynamicFork',
 *       branchExpr: "${workflow.input.fraudTasks}" | null,
 *       resolvedBranches: [                     // null or empty if pure expr
 *         { name: "fraudA", childIds: [canvasId, ...] }
 *       ],
 *       joinId: "<canvasId>" | null
 *     }
 *   }
 *
 * DO_WHILE node:
 *   {
 *     id, type: 'loop',
 *     _containerChildren: {
 *       kind: 'doWhile',
 *       bodyIds: [canvasId, canvasId, ...]
 *     },
 *     // Back-compat: `_loopBody: [...]` is ALSO emitted with the same
 *     // array, so legacy `applyDoWhileContainerSizing` continues to work
 *     // when `features.containerModel` is OFF.
 *     _loopBody: [canvasId, ...]
 *   }
 *
 * SUB_WORKFLOW node:
 *   {
 *     id, type: 'sub_workflow',
 *     _containerChildren: {
 *       kind: 'subWorkflow',
 *       referencedWorkflow: "health_check_sub",
 *       referencedVersion: 3 | null,
 *       embeddedIds: [canvasId, ...] | []    // empty until #141 ships inline expand
 *     }
 *   }
 *
 * INVARIANTS
 * ──────────
 * 1. Every canvasId listed in `_containerChildren.{casePorts|branches|
 *    bodyIds|embeddedIds}` MUST also appear as a peer entry in the
 *    top-level `nodes` array when `features.containerModel` is OFF
 *    (back-compat with the flat legacy renderer). When the flag is ON,
 *    the IR builder removes those peers and re-nests them.
 * 2. Every listed canvasId MUST be reachable from the container node
 *    via the `connections` graph. Validator `validateContainerHint`
 *    checks this.
 * 3. A canvasId appears in AT MOST ONE container's children list.
 *    Cross-container children are a converter bug.
 * 4. `convergenceId` / `joinId` are NOT listed in children arrays —
 *    they are the container's output terminus, not interior members.
 * 5. For SWITCH, case-port keys use the Conductor case names verbatim
 *    (e.g., "valid", "auto") with the literal string `"default"` for
 *    the default case. The internal `"__default__"` token documented
 *    in ConductorToCanvasConverter MUST be normalized to `"default"`
 *    before emission.
 *
 * FEATURE-FLAG GATING
 * ───────────────────
 *   features.containerModel OFF (default): frontend ignores
 *     `_containerChildren` entirely. Flat peer rendering.
 *   features.containerModel ON: frontend uses the hint in flatToIR to
 *     nest children inside their container.
 *
 * The backend emits the hint UNCONDITIONALLY (independent of the
 * frontend flag). Its presence is a pure additive field on the JSON
 * contract — safe for any existing consumer.
 *
 * See docs/R3_CONTAINER_MODEL_REWORK.md for the broader rework.
 */

// ───────────────────────────────────────────────────────────────────
//  Hint-kind string enum — keep in lockstep with backend emitter
// ───────────────────────────────────────────────────────────────────

/**
 * Allowed values of `_containerChildren.kind`. Match the frontend IR
 * kinds (`utils/ir/kinds.js`) except Leaf/Start/End/Terminate which
 * aren't containers.
 *
 * @readonly
 * @enum {string}
 */
export const CONTAINER_HINT_KIND = Object.freeze({
  SWITCH:       'switch',
  FORK:         'fork',
  DYNAMIC_FORK: 'dynamicFork',
  DO_WHILE:     'doWhile',
  SUB_WORKFLOW: 'subWorkflow',
});

/** Full set of valid kinds — used by validators. */
export const CONTAINER_HINT_KINDS = Object.freeze(
  Object.values(CONTAINER_HINT_KIND)
);

/** Field name on the flat-canvas node where the hint lives. */
export const CONTAINER_HINT_FIELD = '_containerChildren';

/** Field name of the legacy DO_WHILE-only hint (kept for back-compat). */
export const LEGACY_LOOP_BODY_FIELD = '_loopBody';

/**
 * Conductor-internal default-case token. Never appears in a well-formed
 * hint — ConductorToCanvasConverter MUST normalize this to `"default"`
 * before emission. Exported so tests can assert on it if they need.
 */
export const INTERNAL_DEFAULT_TOKEN = '__default__';

/** Normalized case-port name for the default branch of a SWITCH. */
export const DEFAULT_CASE_PORT = 'default';

// ───────────────────────────────────────────────────────────────────
//  Reader helpers
// ───────────────────────────────────────────────────────────────────

/**
 * Read the typed hint off a flat-canvas node, or `null` if absent / ill-formed.
 * Defensive — never throws. Callers can rely on the returned shape being
 * one of the five documented variants (kind-discriminated) or `null`.
 *
 * @param {object} node  Flat canvas node (from `{nodes, connections}`).
 * @returns {ContainerHint | null}
 *
 * @typedef {SwitchHint | ForkHint | DynamicForkHint | DoWhileHint | SubWorkflowHint} ContainerHint
 * @typedef {{ kind: 'switch', casePorts: Object.<string, string[]>, convergenceId: (string|null) }} SwitchHint
 * @typedef {{ kind: 'fork', branches: Array.<{name: string, childIds: string[]}>, joinId: (string|null) }} ForkHint
 * @typedef {{ kind: 'dynamicFork', branchExpr: (string|null), resolvedBranches: (Array.<{name:string,childIds:string[]}>|null), joinId: (string|null) }} DynamicForkHint
 * @typedef {{ kind: 'doWhile', bodyIds: string[] }} DoWhileHint
 * @typedef {{ kind: 'subWorkflow', referencedWorkflow: string, referencedVersion: (number|null), embeddedIds: string[] }} SubWorkflowHint
 */
export function getContainerHint(node) {
  if (!node || typeof node !== 'object') return null;
  const raw = node[CONTAINER_HINT_FIELD];
  if (!raw || typeof raw !== 'object') return null;
  if (!CONTAINER_HINT_KINDS.includes(raw.kind)) return null;
  return raw;
}

/**
 * Collect every canvasId referenced as a child across all five hint
 * shapes. Used by `flatToIR` to decide which peer nodes to lift out of
 * the top-level list, and by validators to detect duplicates or dangling
 * refs.
 *
 * Terminal nodes (convergenceId for SWITCH, joinId for FORK) are
 * returned SEPARATELY via `getContainerTerminusId` — they're container
 * output points, not interior members.
 *
 * @param {ContainerHint} hint
 * @returns {string[]}  canvasIds of interior children, in document order
 */
export function getContainerChildIds(hint) {
  if (!hint || typeof hint !== 'object') return [];
  switch (hint.kind) {
    case CONTAINER_HINT_KIND.SWITCH: {
      if (!hint.casePorts || typeof hint.casePorts !== 'object') return [];
      const out = [];
      for (const port of Object.keys(hint.casePorts)) {
        const list = hint.casePorts[port];
        if (Array.isArray(list)) out.push(...list.filter(isNonEmptyString));
      }
      return out;
    }
    case CONTAINER_HINT_KIND.FORK: {
      if (!Array.isArray(hint.branches)) return [];
      const out = [];
      for (const branch of hint.branches) {
        if (branch && Array.isArray(branch.childIds)) {
          out.push(...branch.childIds.filter(isNonEmptyString));
        }
      }
      return out;
    }
    case CONTAINER_HINT_KIND.DYNAMIC_FORK: {
      if (!Array.isArray(hint.resolvedBranches)) return [];
      const out = [];
      for (const branch of hint.resolvedBranches) {
        if (branch && Array.isArray(branch.childIds)) {
          out.push(...branch.childIds.filter(isNonEmptyString));
        }
      }
      return out;
    }
    case CONTAINER_HINT_KIND.DO_WHILE: {
      return Array.isArray(hint.bodyIds)
        ? hint.bodyIds.filter(isNonEmptyString)
        : [];
    }
    case CONTAINER_HINT_KIND.SUB_WORKFLOW: {
      return Array.isArray(hint.embeddedIds)
        ? hint.embeddedIds.filter(isNonEmptyString)
        : [];
    }
    default:
      return [];
  }
}

/**
 * Return the container's terminus (convergence pill or JOIN node) id,
 * or `null` if none. Terminus nodes are NOT interior members but are
 * rendered inside the container's bbox; the IR layout pass treats them
 * as the container's output port anchor.
 *
 * @param {ContainerHint} hint
 * @returns {string | null}
 */
export function getContainerTerminusId(hint) {
  if (!hint || typeof hint !== 'object') return null;
  switch (hint.kind) {
    case CONTAINER_HINT_KIND.SWITCH:
      return isNonEmptyString(hint.convergenceId) ? hint.convergenceId : null;
    case CONTAINER_HINT_KIND.FORK:
    case CONTAINER_HINT_KIND.DYNAMIC_FORK:
      return isNonEmptyString(hint.joinId) ? hint.joinId : null;
    default:
      return null;
  }
}

// ───────────────────────────────────────────────────────────────────
//  Validator — used in tests + lossy-conversion-report surfacing
// ───────────────────────────────────────────────────────────────────

/**
 * Validate a single hint against its documented shape. Returns an array
 * of human-readable error strings; empty array = well-formed.
 *
 * Does NOT check cross-node invariants (duplicates across containers,
 * membership in top-level `nodes`, reachability via `connections`) —
 * that's `validateContainerHintsInGraph`.
 *
 * @param {unknown} hint
 * @returns {string[]}
 */
export function validateContainerHint(hint) {
  const errs = [];
  if (!hint || typeof hint !== 'object') {
    errs.push('hint must be a non-null object');
    return errs;
  }
  if (!CONTAINER_HINT_KINDS.includes(hint.kind)) {
    errs.push(
      `hint.kind must be one of ${CONTAINER_HINT_KINDS.join(', ')} — got ${JSON.stringify(hint.kind)}`
    );
    return errs;
  }

  switch (hint.kind) {
    case CONTAINER_HINT_KIND.SWITCH: {
      if (!hint.casePorts || typeof hint.casePorts !== 'object') {
        errs.push('switch hint must have a casePorts object');
      } else {
        for (const [port, list] of Object.entries(hint.casePorts)) {
          if (port === INTERNAL_DEFAULT_TOKEN) {
            errs.push(
              `switch hint must not use internal token "${INTERNAL_DEFAULT_TOKEN}" — ` +
                `normalize to "${DEFAULT_CASE_PORT}" in the converter`
            );
          }
          if (!Array.isArray(list)) {
            errs.push(`switch hint casePorts["${port}"] must be an array`);
            continue;
          }
          for (const id of list) {
            if (!isNonEmptyString(id)) {
              errs.push(`switch hint casePorts["${port}"] contains non-string entry`);
            }
          }
        }
      }
      if (hint.convergenceId != null && !isNonEmptyString(hint.convergenceId)) {
        errs.push('switch hint convergenceId must be a non-empty string or null');
      }
      break;
    }
    case CONTAINER_HINT_KIND.FORK: {
      if (!Array.isArray(hint.branches)) {
        errs.push('fork hint must have a branches array');
      } else {
        hint.branches.forEach((b, i) => {
          if (!b || typeof b !== 'object') {
            errs.push(`fork hint branches[${i}] must be an object`);
            return;
          }
          if (!isNonEmptyString(b.name)) {
            errs.push(`fork hint branches[${i}].name must be a non-empty string`);
          }
          if (!Array.isArray(b.childIds)) {
            errs.push(`fork hint branches[${i}].childIds must be an array`);
          }
        });
      }
      if (hint.joinId != null && !isNonEmptyString(hint.joinId)) {
        errs.push('fork hint joinId must be a non-empty string or null');
      }
      break;
    }
    case CONTAINER_HINT_KIND.DYNAMIC_FORK: {
      if (hint.branchExpr != null && !isNonEmptyString(hint.branchExpr)) {
        errs.push('dynamicFork hint branchExpr must be a non-empty string or null');
      }
      if (hint.resolvedBranches != null && !Array.isArray(hint.resolvedBranches)) {
        errs.push('dynamicFork hint resolvedBranches must be an array or null');
      }
      if (Array.isArray(hint.resolvedBranches)) {
        hint.resolvedBranches.forEach((b, i) => {
          if (!b || typeof b !== 'object') {
            errs.push(`dynamicFork hint resolvedBranches[${i}] must be an object`);
            return;
          }
          if (!isNonEmptyString(b.name)) {
            errs.push(`dynamicFork hint resolvedBranches[${i}].name must be non-empty`);
          }
          if (!Array.isArray(b.childIds)) {
            errs.push(`dynamicFork hint resolvedBranches[${i}].childIds must be an array`);
          }
        });
      }
      if (hint.joinId != null && !isNonEmptyString(hint.joinId)) {
        errs.push('dynamicFork hint joinId must be a non-empty string or null');
      }
      break;
    }
    case CONTAINER_HINT_KIND.DO_WHILE: {
      if (!Array.isArray(hint.bodyIds)) {
        errs.push('doWhile hint bodyIds must be an array');
      } else {
        hint.bodyIds.forEach((id, i) => {
          if (!isNonEmptyString(id)) {
            errs.push(`doWhile hint bodyIds[${i}] must be a non-empty string`);
          }
        });
      }
      break;
    }
    case CONTAINER_HINT_KIND.SUB_WORKFLOW: {
      if (!isNonEmptyString(hint.referencedWorkflow)) {
        errs.push('subWorkflow hint referencedWorkflow must be a non-empty string');
      }
      if (
        hint.referencedVersion != null &&
        !(Number.isInteger(hint.referencedVersion) && hint.referencedVersion > 0)
      ) {
        errs.push('subWorkflow hint referencedVersion must be a positive integer or null');
      }
      if (!Array.isArray(hint.embeddedIds)) {
        errs.push('subWorkflow hint embeddedIds must be an array (may be empty)');
      }
      break;
    }
    default:
      // Unreachable — kind was validated above.
      break;
  }

  return errs;
}

/**
 * Validate container hints across a whole flat-canvas graph. Checks:
 *  - each hint is individually well-formed (`validateContainerHint`)
 *  - every child/terminus id exists in the top-level `nodes` array
 *  - no canvasId appears in more than one container's children list
 *  - every child is reachable from its container via `connections`
 *    (short BFS bounded at 32 hops to catch disconnected peers)
 *
 * Returns `{ ok: boolean, issues: Array.<{ nodeId: string, message: string }> }`.
 *
 * @param {{nodes: object[], connections: object[]}} canvasGraph
 * @returns {{ok: boolean, issues: Array.<{nodeId: string, message: string}>}}
 */
export function validateContainerHintsInGraph(canvasGraph) {
  const issues = [];
  if (!canvasGraph || typeof canvasGraph !== 'object') {
    issues.push({ nodeId: '', message: 'canvasGraph must be a non-null object' });
    return { ok: false, issues };
  }
  const nodes = Array.isArray(canvasGraph.nodes) ? canvasGraph.nodes : [];
  const connections = Array.isArray(canvasGraph.connections) ? canvasGraph.connections : [];
  const nodeById = new Map();
  for (const n of nodes) {
    if (n && isNonEmptyString(n.id)) nodeById.set(n.id, n);
  }

  const childOwnership = new Map(); // canvasId → containerId (first owner)

  for (const node of nodes) {
    const hint = getContainerHint(node);
    if (!hint) continue;

    // Per-hint shape check.
    const shapeErrs = validateContainerHint(hint);
    for (const e of shapeErrs) issues.push({ nodeId: node.id, message: e });
    if (shapeErrs.length > 0) continue;

    // Child ids must exist in the graph and not be owned elsewhere.
    const childIds = getContainerChildIds(hint);
    for (const cid of childIds) {
      if (!nodeById.has(cid)) {
        issues.push({
          nodeId: node.id,
          message: `container child "${cid}" is not present in top-level nodes`,
        });
        continue;
      }
      const owner = childOwnership.get(cid);
      if (owner && owner !== node.id) {
        issues.push({
          nodeId: node.id,
          message: `container child "${cid}" is already owned by container "${owner}"`,
        });
      } else {
        childOwnership.set(cid, node.id);
      }
    }

    // Terminus (convergence/join) must exist in the graph if specified,
    // but MUST NOT be listed as an interior child.
    const terminusId = getContainerTerminusId(hint);
    if (terminusId != null) {
      if (!nodeById.has(terminusId)) {
        issues.push({
          nodeId: node.id,
          message: `container terminus "${terminusId}" is not present in top-level nodes`,
        });
      }
      if (childIds.includes(terminusId)) {
        issues.push({
          nodeId: node.id,
          message: `container terminus "${terminusId}" must not also appear in interior children`,
        });
      }
    }

    // Reachability: every interior child should be reachable from the
    // container via `connections` within a bounded hop budget. A hint
    // claiming children that aren't connected is a converter bug.
    const reachable = bfsReachable(node.id, connections, 32);
    for (const cid of childIds) {
      if (!reachable.has(cid)) {
        issues.push({
          nodeId: node.id,
          message: `container child "${cid}" is not reachable from container via connections`,
        });
      }
    }
  }

  return { ok: issues.length === 0, issues };
}

// ───────────────────────────────────────────────────────────────────
//  Private helpers
// ───────────────────────────────────────────────────────────────────

function isNonEmptyString(x) {
  return typeof x === 'string' && x.length > 0;
}

/**
 * Bounded BFS over the connections list — cheap enough to run per
 * container at graph build time. Hop budget guards against pathological
 * graphs (we only need to confirm membership of a container's direct
 * reachable subtree, not full graph closure).
 */
function bfsReachable(startId, connections, hopBudget) {
  const adj = new Map();
  for (const c of connections) {
    if (!c || !isNonEmptyString(c.source) || !isNonEmptyString(c.target)) continue;
    if (!adj.has(c.source)) adj.set(c.source, []);
    adj.get(c.source).push(c.target);
  }
  const visited = new Set();
  const queue = [[startId, 0]];
  while (queue.length > 0) {
    const [id, hops] = queue.shift();
    if (visited.has(id) || hops > hopBudget) continue;
    visited.add(id);
    const nexts = adj.get(id) || [];
    for (const n of nexts) {
      if (!visited.has(n)) queue.push([n, hops + 1]);
    }
  }
  visited.delete(startId); // don't count the container itself
  return visited;
}
